import React, { Component } from 'react'
import PropTypes from 'prop-types'
import SubmitAccordionHeader from './SubmitAccordionHeader'
import DisplayInformationContent from './DisplayInformationContent'
import DefinedScopesContent from './DefinedScopesContent'
import ServiceTermsContent from './ServiceTermsContent'
import SubmissionNotes from './SubmissionNotes'
import ConditionalRender from 'components/ConditionalRender'
import AppSubmittedModal from './AppSubmittedModal'
import AppPublishedModal from './AppPublishedModal'
import AppUnderReview from './AppUnderReview'
import AppPublished from './AppPublished'
import AppPendingPublish from './AppPendingPublish'
import { Loader, Button } from '@driftt/tide-core'
import { findMissingFields } from 'utils/validation'
import {
  fetchApp,
  updateAppCategory,
  updateAppPricing,
  fetchAppScopes,
  fetchAppSubmissionData,
  updateTermsOfServiceFlag,
  updateSubmissionNotes,
  submitAppForReview,
  publishApp
} from 'api/developerApi'
import { isEmpty } from 'ramda'
import { startCase } from 'lodash'
import './style.css'

const DistributionStatus = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PENDING_PUBLISH: 'PENDING_PUBLISH',
  INTERNAL: 'INTERNAL',
  PUBLIC: 'PUBLIC'
}

class AppSubmitView extends Component {
  static propTypes = {
    appId: PropTypes.string.isRequired,
    showErrorNotification: PropTypes.func.isRequired
  }

  state = {
    isInitializing: true,
    app: {},
    scopes: [],
    submissionData: {},
    appSubmittedModalVisible: false,
    appPublishedModalVisible: false,
    isSubmittingApp: false,
    isPublishingApp: false
  }

  componentDidMount() {
    this.fetchData()
  }

  fetchData = () => {
    const { appId, showErrorNotification } = this.props

    const onDone = ([app, scopes, submissionData]) => {
      this.setState({ app, scopes, submissionData, isInitializing: false })
    }
    const onError = () => {
      this.setState({ isInitializing: false })
      showErrorNotification('Oops, something went wrong. Please refresh the page.')
    }

    Promise.all([
      fetchApp(appId),
      fetchAppScopes(appId),
      fetchAppSubmissionData(appId)
    ])
      .then(onDone)
      .catch(onError)
  }

  updateCategory = (category) => {
    const { appId } = this.props
    const onDone = (app) => this.setState({ app })

    return updateAppCategory({ appId, category }).then(onDone)
  }

  updatePricing = (paidStatus) => {
    const { appId } = this.props
    const onDone = (app) => this.setState({ app })

    return updateAppPricing({ appId, paidStatus }).then(onDone)
  }

  findMissingAppFields = () => {
    return findMissingFields({
      requiredPropertyNames: ['name', 'shortDescription', 'longDescription', 'iconUrl', 'category'],
      objectToValidate: this.state.app
    }).map(startCase)
  }

  hasScopes = () => {
    const { scopes } = this.state
    return !isEmpty(scopes)
  }

  hasAgreedToTerms = () => {
    const { submissionData } = this.state
    return !!submissionData.agreesToTermsOfService
  }

  canSubmitApp = () => {
    return this.hasScopes() && this.hasAgreedToTerms()
  }

  submitApp = () => {
    const { appId, showErrorNotification } = this.props

    const missingFields = this.findMissingAppFields()
    if (missingFields && missingFields.length > 0) {
      const fieldString = missingFields.join(', ')
      showErrorNotification(`The following app fields should be set: ${fieldString}`)
      return
    }

    const onDone = (app) => {
      this.setState({
        app,
        isSubmittingApp: false,
        appSubmittedModalVisible: true
      })
    }

    const onError = () => {
      this.setState({ isSubmittingApp: false })
      showErrorNotification('Ooops, something went wrong. Please try again.')
    }

    const onStateSet = () => submitAppForReview(appId)
      .then(onDone)
      .catch(onError)

    this.setState({ isSubmittingApp: true }, onStateSet)
  }

  updateTermsOfServiceFlag = (agreesToTermsOfService) => {
    const { appId } = this.props
    const onDone = (submissionData) => this.setState({ submissionData })

    return updateTermsOfServiceFlag({ appId, agreesToTermsOfService })
      .then(onDone)
  }

  hasNotes = () => {
    const { submissionData } = this.state
    return !isEmpty(submissionData.submissionNotes)
  }

  updateSubmissionNotes = (submissionNotes) => {
    const { appId } = this.props
    return updateSubmissionNotes({ appId, submissionNotes })
  }

  closeAppSubmittedModal = () => {
    this.setState({ appSubmittedModalVisible: false })
  }

  closeAppPublishedModal = () => {
    this.setState({ appPublishedModalVisible: false })
  }

  canRenderSubmissionView = () => {
    const { app } = this.state
    return app.distributionStatus !== DistributionStatus.PUBLIC &&
      app.distributionStatus !== DistributionStatus.PENDING_APPROVAL &&
      app.distributionStatus !== DistributionStatus.PENDING_PUBLISH
  }

  publishApp = () => {
    const { appId, showErrorNotification } = this.props

    const onDone = (app) => {
      this.setState({
        app,
        isPublishingApp: false,
        appPublishedModalVisible: true
      })
    }

    const onError = () => {
      this.setState({ isPublishingApp: false })
      showErrorNotification('Ooops, something went wrong. Please try again.')
    }

    const onStateSet = () => publishApp(appId)
      .then(onDone)
      .catch(onError)

    this.setState({ isPublishingApp: true }, onStateSet)
  }

  render() {
    const { appId, showErrorNotification } = this.props
    const {
      isInitializing,
      app,
      submissionData,
      appSubmittedModalVisible,
      appPublishedModalVisible,
      isSubmittingApp,
      isPublishingApp
    } = this.state

    return (
      <div className="app-submit-view">
        <span className="page-title">
          Submit to Drift Directory
        </span>
        <p className="page-description">
          Use this page to publicly submit your app or integration to the Drift Directory so that others can start using your app in their own Drift accounts.
          Once you click Submit, we’ll review your app and let you know when it’s ready for public distribution!
        </p>

        {/* TODO: Move this into a component */}
        <ConditionalRender condition={this.canRenderSubmissionView()}>
          <span className="app-submit-view-submission-guidelines-title">
            Submission Guidelines
          </span>
          <p className="app-submit-view-submission-guidelines-description">
            Here’s a checklist so you can make sure you have everything you need in order to submit your app to the Drift App Directory!
            Check out our submissions guidelines for more information.
          </p>

          <SubmitAccordionHeader
            stepId="1"
            name="Display Information"
            isCheckedOff={this.findMissingAppFields().length === 0}
          />
          <DisplayInformationContent
            appId={appId}
            category={app.category}
            paidStatus={app.paidStatus}
            onCategoryUpdated={this.updateCategory}
            onPricingUpdated={this.updatePricing}
            showErrorNotification={showErrorNotification}
          />

          <SubmitAccordionHeader
            stepId="2"
            name="Defined Scopes"
            isCheckedOff={this.hasScopes()}
          />
          <DefinedScopesContent appId={appId} />

          <SubmitAccordionHeader
            stepId="3"
            name="Terms of Service"
            isCheckedOff={this.hasAgreedToTerms()}
          />
          <ServiceTermsContent
            appId={appId}
            agreesToTermsOfService={submissionData.agreesToTermsOfService}
            updateTermsOfServiceFlag={this.updateTermsOfServiceFlag}
            showErrorNotification={showErrorNotification}
          />

          <SubmitAccordionHeader
            stepId="4"
            name="App Submission Notes"
            isCheckedOff={this.hasNotes()}
          />
          <SubmissionNotes
            appId={appId}
            notes={submissionData.submissionNotes}
            updateNotes={this.updateSubmissionNotes}
            showErrorNotification={showErrorNotification}
          />

          <Button
            className="app-submit-button"
            type="primary"
            size="small"
            loading={isSubmittingApp}
            disabled={!this.canSubmitApp()}
            onClick={this.submitApp}>
            Submit App for Review
          </Button>

          <ConditionalRender
            condition={isInitializing}
            className="app-submit-view-loader">
            <Loader />
          </ConditionalRender>
        </ConditionalRender>

        <ConditionalRender condition={app.distributionStatus === DistributionStatus.PENDING_APPROVAL}>
          <AppUnderReview />
        </ConditionalRender>

        <ConditionalRender condition={app.distributionStatus === DistributionStatus.PENDING_PUBLISH}>
          <AppPendingPublish
            isPublishingApp={isPublishingApp}
            onPublishClick={this.publishApp}
          />
        </ConditionalRender>

        <ConditionalRender condition={app.distributionStatus === DistributionStatus.PUBLIC}>
          <AppPublished />
        </ConditionalRender>

        <ConditionalRender condition={appPublishedModalVisible}>
          <AppPublishedModal onCloseInitiated={this.closeAppPublishedModal} />
        </ConditionalRender>

        <ConditionalRender condition={appSubmittedModalVisible}>
          <AppSubmittedModal onCloseInitiated={this.closeAppSubmittedModal} />
        </ConditionalRender>

      </div>
    )
  }
}

export default AppSubmitView
